import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl, endpoints } from '../config/api';

const CommentsSection = () => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [postTitle, setPostTitle] = useState('');
  const [nickname, setNickname] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyNickname, setReplyNickname] = useState('');
  const [replyIsAnonymous, setReplyIsAnonymous] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      const response = await axios.get(getApiUrl(endpoints.comments));
      setComments(response.data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  const validateUsername = (username) => {
    if (!username || username.trim() === '') return true; // Empty is allowed
    // Must start with @ and have at least one character after
    return /^@[a-zA-Z0-9_]+$/.test(username.trim());
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!postTitle.trim()) return;

    // If not anonymous, X handle is required
    if (!isAnonymous) {
      if (!nickname.trim()) {
        alert('Please enter your X handle (e.g., @username) or choose anonymous mode');
        return;
      }
      if (!validateUsername(nickname.trim())) {
        alert('Please enter a valid X username (e.g., @username) or choose anonymous mode');
        return;
      }
    }

    setLoading(true);
    try {
      const finalNickname = isAnonymous ? '' : nickname.trim();
      const response = await axios.post(getApiUrl(endpoints.comments), {
        content: newComment.trim() || '',
        title: postTitle.trim(),
        nickname: finalNickname || 'Anonymous'
      });
      
      setComments([response.data, ...comments]);
      setNewComment('');
      setPostTitle('');
      setNickname('');
      setIsAnonymous(false);
    } catch (err) {
      console.error('Error posting comment:', err);
    } finally {
      setLoading(false);
    }
  };

  const submitReply = async (commentId) => {
    if (!replyContent.trim()) return;

    // If not anonymous, X handle is required
    if (!replyIsAnonymous) {
      if (!replyNickname.trim()) {
        alert('Please enter your X handle (e.g., @username) or choose anonymous mode');
        return;
      }
      if (!validateUsername(replyNickname.trim())) {
        alert('Please enter a valid X username (e.g., @username) or choose anonymous mode');
        return;
      }
    }

    setReplyLoading(true);
    try {
      const finalReplyNickname = replyIsAnonymous ? '' : replyNickname.trim();
      const response = await axios.post(getApiUrl(`${endpoints.comments}/${commentId}/replies`), {
        content: replyContent.trim(),
        nickname: finalReplyNickname || 'Anonymous'
      });
      
      // Update the comment with the new reply
      setComments(comments.map(comment => 
        comment.id === commentId 
          ? { ...comment, replies: [...comment.replies, response.data] }
          : comment
      ));
      
      setReplyContent('');
      setReplyNickname('');
      setReplyIsAnonymous(false);
      setReplyingTo(null);
    } catch (err) {
      console.error('Error posting reply:', err);
    } finally {
      setReplyLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const shareToX = (discussion) => {
    const url = window.location.href;
    const text = `OrAi Desire: "${discussion.title || 'Untitled Desire'}"
    
${discussion.content ? discussion.content.substring(0, 200) + (discussion.content.length > 200 ? '...' : '') : 'Join the conversation!'}`;
    
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const formatContentWithLinks = (content) => {
    if (!content) return '';
    
    // Regex to match X/Twitter URLs
    const xUrlRegex = /(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/[^\s]+/gi;
    
    return content.split(xUrlRegex).map((part, index) => {
      if (xUrlRegex.test(part) || part.match(/^(twitter\.com|x\.com)/)) {
        const fullUrl = part.startsWith('http') ? part : `https://${part}`;
        return (
          <a 
            key={index} 
            href={fullUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="x-link"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const formatUsername = (username) => {
    if (!username || username === 'Anonymous Oracle') return 'Anonymous';
    
    // If username starts with @, make it a clickable X link
    if (username.startsWith('@')) {
      const handle = username.substring(1);
      return (
        <a 
          href={`https://x.com/${handle}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="x-handle-link"
        >
          {username}
        </a>
      );
    }
    
    return username;
  };

  return (
    <section className="section blog-section">
      <div className="blog-header">
        <h2>Desires, expectations, what do you really want?</h2>
        <p className="blog-subtitle">Join the community discussion • Share insights • Connect with fellow OrAi users</p>
      </div>
      
      {/* New Discussion Form */}
      <div className="blog-post-form">
        <h3>Start a Discussion</h3>
        
        <form onSubmit={submitComment} className="comment-form">
          <div className="form-group">
            <label htmlFor="post-title">Title *</label>
            <input
              id="post-title"
              type="text"
              className="title-input"
              placeholder="What would you like to discuss? (e.g., 'Thoughts on recent market trends')"
              value={postTitle}
              onChange={(e) => setPostTitle(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="author-name">Your Identity</label>
            <div className="identity-options">
              <label className="anonymous-toggle">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => {
                    setIsAnonymous(e.target.checked);
                    if (e.target.checked) {
                      setNickname('');
                    }
                  }}
                />
                <span className="toggle-text">Post anonymously</span>
              </label>
              {!isAnonymous && (
                                  <input
                    id="author-name"
                    type="text"
                    className="nickname-input title-input"
                    placeholder="Enter your X handle (e.g., @username) - Required"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    required
                  />
              )}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="post-content">Content (optional)</label>
            <textarea
              id="post-content"
              className="comment-input blog-textarea"
              placeholder="Share your thoughts, ask questions, or start a conversation... (optional)"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={6}
            />
          </div>
          
          <button
            type="submit"
            className="submit-btn blog-submit"
            disabled={loading || !postTitle.trim()}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Publishing...
              </>
            ) : (
              'Start Discussion'
            )}
          </button>
        </form>
      </div>

      {/* Discussion Topics */}
      <div className="blog-posts">
        {comments.length === 0 ? (
          <div className="empty-state">
            <h3>No Discussions Yet</h3>
            <p>Be the first to start a conversation with the community.</p>
          </div>
        ) : (
          <>            
            <div className="posts-list">
              {comments.map((comment, index) => (
                <article key={comment.id} className="blog-post discussion-topic">
                  <div className="post-header">
                    <h3 className="post-title">
                      {comment.title || `Discussion ${comments.length - index}`}
                    </h3>
                    <div className="post-meta">
                      <span className="post-author">
                        Started by {formatUsername(comment.nickname)}
                      </span>
                      <div className="post-meta-right">
                        {comment.replies && comment.replies.length > 0 && (
                          <span className="activity-indicator">
                            🔥 {comment.replies.length} {comment.replies.length === 1 ? 'response' : 'responses'}
                          </span>
                        )}
                        <span className="post-date">
                          {formatTimestamp(comment.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {comment.content && (
                    <div className="post-content">
                      <p>{formatContentWithLinks(comment.content)}</p>
                    </div>
                  )}
                  
                  <div className="post-actions">
                    <div className="action-buttons">
                      <button
                        className="reply-btn"
                        onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                      >
                        {replyingTo === comment.id ? 'Cancel' : 'Comment'}
                      </button>
                      <button
                        className="share-btn x-share-btn"
                        onClick={() => shareToX(comment)}
                        title="Share on X"
                      >
                        𝕏 Share
                      </button>
                    </div>
                    {comment.replies && comment.replies.length > 0 && (
                      <span className="reply-count">
                        {comment.replies.length} {comment.replies.length === 1 ? 'Response' : 'Responses'}
                      </span>
                    )}
                  </div>

                  {/* Discussion Reply Form */}
                  {replyingTo === comment.id && (
                    <div className="reply-form">
                      <h4>Add to Discussion</h4>
                      <div className="reply-identity-options">
                        <label className="anonymous-toggle">
                          <input
                            type="checkbox"
                            checked={replyIsAnonymous}
                            onChange={(e) => {
                              setReplyIsAnonymous(e.target.checked);
                              if (e.target.checked) {
                                setReplyNickname('');
                              }
                            }}
                          />
                          <span className="toggle-text">Reply anonymously</span>
                        </label>
                        {!replyIsAnonymous && (
                                                      <input
                              type="text"
                              className="nickname-input"
                              placeholder="Enter your X handle (e.g., @username) - Required"
                              value={replyNickname}
                              onChange={(e) => setReplyNickname(e.target.value)}
                              required
                            />
                        )}
                      </div>
                      <textarea
                        className="reply-input"
                        placeholder="Share your thoughts on this topic..."
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        rows={3}
                      />
                      <button
                        className="reply-submit-btn"
                        onClick={() => submitReply(comment.id)}
                        disabled={replyLoading || !replyContent.trim()}
                      >
                        {replyLoading ? (
                          <>
                            <span className="loading-spinner"></span>
                            Posting...
                          </>
                        ) : (
                          'Add Response'
                        )}
                      </button>
                    </div>
                  )}

                  {/* Discussion Responses */}
                  {comment.replies && comment.replies.length > 0 && (
                    <div className="post-replies">
                      <h4>Comments</h4>
                      {comment.replies.map((reply) => (
                        <div key={reply.id} className="reply">
                          <div className="reply-header">
                            <span className="reply-author">
                              {formatUsername(reply.nickname)}
                            </span>
                            <span className="reply-time">
                              {formatTimestamp(reply.timestamp)}
                            </span>
                          </div>
                          <div className="reply-content">{formatContentWithLinks(reply.content)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default CommentsSection; 